/**
 * AgentSubworkflowView — Sub-workflow browser for the `subworkflows` category
 * in `OperationPickerPanel` (agent-tools mode).
 *
 * Lists callable workflows in the current Logic App (those with a single
 * Request/Http trigger). Clicking a workflow synthesizes an `invoke_workflow`
 * tool on the target agent (or on a freshly created wrapping agent for the
 * standalone toolbar entry point).
 */

import { useMemo } from 'react';
import { Text, makeStyles, tokens } from '@coreai/fluentui-react';
import {
  OrganizationHorizontalRegular,
  PersonChatRegular,
  WarningRegular,
} from '@fluentui/react-icons';
import {
  useCallableWorkflows,
  getCallableWorkflowInvocationIssue,
  type CallableWorkflow,
} from '@/hooks/useCallableWorkflows';
import { PanelStatus } from './PanelStatus';

const useStyles = makeStyles({
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    borderRadius: tokens.borderRadiusMedium,
    border: '1px solid transparent',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      border: `1px solid ${tokens.colorNeutralStroke1}`,
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '2px',
    },
  },
  rowDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
    ':hover': {
      backgroundColor: 'transparent',
      border: '1px solid transparent',
    },
  },
  iconCell: {
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorPaletteGreenBackground3,
    color: tokens.colorNeutralForegroundOnBrand,
    flexShrink: 0,
  },
  content: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  name: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  description: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  warningRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    color: tokens.colorPaletteDarkOrangeForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  warningIcon: {
    fontSize: tokens.fontSizeBase300,
    flexShrink: 0,
  },
});

const getDescriptionFor = (workflow: CallableWorkflow): string => {
  if (workflow.kind === 'Agent') return 'Agent workflow';
  if (workflow.triggerMethod) return `${workflow.triggerMethod.toUpperCase()} trigger`;
  return 'Workflow';
};

export interface AgentSubworkflowViewProps {
  /** Current workflow name — excluded from the list to prevent self-invocation. */
  currentWorkflowName?: string | undefined;
  /** Optional case-insensitive name filter. */
  searchQuery?: string;
  onWorkflowSelect: (workflow: CallableWorkflow) => void;
}

export const AgentSubworkflowView = ({
  currentWorkflowName,
  searchQuery,
  onWorkflowSelect,
}: AgentSubworkflowViewProps) => {
  const styles = useStyles();
  const { data, isLoading, isError } = useCallableWorkflows(currentWorkflowName);

  const filtered = useMemo(() => {
    const q = searchQuery?.trim().toLowerCase() ?? '';
    if (!q) return data;
    return data.filter((w) => w.name.toLowerCase().includes(q));
  }, [data, searchQuery]);

  if (isLoading) {
    return <PanelStatus variant="loading" message="Loading workflows..." />;
  }

  if (isError) {
    return <PanelStatus variant="error" message="Failed to load workflows" />;
  }

  if (filtered.length === 0) {
    const query = searchQuery?.trim();
    return (
      <PanelStatus
        variant="empty"
        icon={<PersonChatRegular />}
        message={
          query
            ? `No workflows match "${query}"`
            : 'No callable workflows in this Logic App'
        }
        {...(!query && {
          detail: 'Workflows with a single Request/Http trigger can be called as tools.',
        })}
      />
    );
  }

  return (
    <div className={styles.list}>
      {filtered.map((workflow) => {
        const issue = getCallableWorkflowInvocationIssue(workflow);
        const disabled = issue !== null;
        const description = issue ?? getDescriptionFor(workflow);
        return (
          <div
            key={workflow.name}
            className={`${styles.row}${disabled ? ` ${styles.rowDisabled}` : ''}`}
            onClick={() => {
              if (!disabled) onWorkflowSelect(workflow);
            }}
            onKeyDown={(e) => {
              if (disabled) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onWorkflowSelect(workflow);
              }
            }}
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled || undefined}
            aria-label={`Use ${workflow.name} as a tool`}
            title={issue ?? undefined}
          >
            <div className={styles.iconCell}>
              <OrganizationHorizontalRegular aria-hidden="true" />
            </div>
            <div className={styles.content}>
              <Text className={styles.name}>{workflow.name}</Text>
              {disabled ? (
                <div className={styles.warningRow}>
                  <WarningRegular className={styles.warningIcon} />
                  <Text size={200}>{description}</Text>
                </div>
              ) : (
                <Text className={styles.description}>{description}</Text>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
